/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
import { HtmlAttributes } from "./Constants/HtmlAttributes";
import { UriConstants } from "./Constants/UriConstants";
import { HtmlAttributes as CommonHtmlAttributes } from "../../../Common/Constants/HtmlAttributes";
var FormUtility = /** @class */ (function () {
    function FormUtility() {
    }
    FormUtility.createFormDescriptorFromPlaceholderElement = function (element) {
        var id = element.getAttribute(HtmlAttributes.formId);
        // Backward compatibility
        if (!id) {
            id = element.getAttribute(HtmlAttributes.formBlockId);
        }
        var formApiBaseUrl = element.getAttribute(HtmlAttributes.formApiBaseUrl);
        var successMessage = element.getAttribute(CommonHtmlAttributes.successMessage);
        var waitlistMessage = element.getAttribute(CommonHtmlAttributes.waitlistMessage);
        var errorMessage = element.getAttribute(CommonHtmlAttributes.errorMessage);
        var notStartedMessage = element.getAttribute(CommonHtmlAttributes.notStartedMessage);
        var closedMessage = element.getAttribute(CommonHtmlAttributes.closedMessage);
        var redirectUrl = element.getAttribute(CommonHtmlAttributes.redirectUrl);
        var preventSubmissionUi = element.getAttribute(CommonHtmlAttributes.preventSubmissionUi) === "true";
        var unboundForm = element.getAttribute(CommonHtmlAttributes.unboundForm) === "true";
        var feedbackPosition = element.getAttribute(CommonHtmlAttributes.feedbackPosition);
        var doNotTrack = element.getAttribute(HtmlAttributes.dataDoNotTrack) &&
            element.getAttribute(HtmlAttributes.dataDoNotTrack).localeCompare("true", undefined, { sensitivity: "accent" }) === 0;
        var prefillAlias = FormUtility.getPrefillAlias();
        var formUrl;
        var submitFormUrl;
        var visitFormUrl;
        var lookupFormUrl;
        var formLoadResponseType;
        var formError;
        if (formApiBaseUrl) {
            var isEventForm = formApiBaseUrl.endsWith("/eventmanagement");
            // In EventManagement service the path is /form/, for LandingPageForms the path is /forms/
            var formPath = isEventForm ? "form" : "forms";
            if (isEventForm) {
                var readableEventId = FormUtility.getReadableEventId(element);
                if (readableEventId) {
                    var eventRegistrationId = FormUtility.getEventRegistrationId();
                    if (eventRegistrationId) {
                        formUrl = this.addParameterToURL(FormUtility.getFormUrl("".concat(formApiBaseUrl, "/event/").concat(readableEventId, "/rendercancellationformhtml")), "eventRegistrationId", eventRegistrationId);
                        submitFormUrl = "".concat(formApiBaseUrl, "/").concat(formPath, "/").concat(id, "/event/").concat(readableEventId, "/cancelregistration");
                    }
                    else {
                        formUrl = FormUtility.getFormUrl("".concat(formApiBaseUrl, "/event/").concat(readableEventId, "/renderformhtml"));
                        submitFormUrl = "".concat(formApiBaseUrl, "/").concat(formPath, "/").concat(id, "/event/").concat(readableEventId, "/registration");
                    }
                    if (prefillAlias) {
                        formUrl = this.addParameterToURL(formUrl, "prefillAliasId", prefillAlias);
                    }
                    formLoadResponseType = "application/json";
                }
                else {
                    formUrl = FormUtility.getFormUrl(element === null || element === void 0 ? void 0 : element.getAttribute(HtmlAttributes.formCachedUrl));
                    formError = "MissingEventParameter";
                    formLoadResponseType = "text/plain";
                }
            }
            else {
                if (prefillAlias) {
                    formUrl = this.addParameterToURL(FormUtility.getFormUrl("".concat(formApiBaseUrl, "/").concat(formPath, "/").concat(id)), UriConstants.prefillAliasId, prefillAlias);
                    formLoadResponseType = "application/json";
                }
                else {
                    formUrl = FormUtility.getFormUrl(element.getAttribute(HtmlAttributes.formCachedUrl));
                    formLoadResponseType = "text/plain";
                }
                submitFormUrl = "".concat(formApiBaseUrl, "/").concat(formPath, "/").concat(id);
            }
            lookupFormUrl = "".concat(formApiBaseUrl, "/").concat(formPath, "/").concat(id, "/lookup");
            // visits should be always sent to LandingPageForms service for now
            var serviceIndex = formApiBaseUrl.lastIndexOf("/");
            if (serviceIndex > 0) {
                visitFormUrl = "".concat(formApiBaseUrl.substring(0, serviceIndex), "/landingpageforms/forms/").concat(id, "/visits");
            }
            else {
                visitFormUrl = "".concat(formApiBaseUrl, "/landingpageforms/forms/").concat(id, "/visits");
            }
        }
        else {
            // Backward compatibility
            if (prefillAlias) {
                formUrl = this.addParameterToURL(FormUtility.getFormUrl(element.getAttribute(HtmlAttributes.formSubmitUrl)), UriConstants.prefillAliasId, prefillAlias);
                formLoadResponseType = "application/json";
            }
            else {
                formUrl = FormUtility.getFormUrl(element.getAttribute(HtmlAttributes.formCachedUrl));
                formLoadResponseType = "text/plain";
            }
            submitFormUrl = element.getAttribute(HtmlAttributes.formSubmitUrl);
            visitFormUrl = "".concat(submitFormUrl, "/visits");
            lookupFormUrl = "".concat(submitFormUrl, "/lookup");
        }
        var form = {
            FormId: id,
            FormUrl: formUrl,
            DoNotTrack: doNotTrack,
            SubmitFormUrl: submitFormUrl,
            FormPlaceholders: [{ Element: element }],
            FormVisitUrl: visitFormUrl,
            FormLookupUrl: lookupFormUrl,
            FormApiBaseUrl: formApiBaseUrl,
            FormSubmitProperties: {
                SuccessMessage: successMessage,
                WaitlistMessage: waitlistMessage,
                NotStartedMessage: notStartedMessage,
                ClosedMessage: closedMessage,
                ErrorMessage: errorMessage,
                RedirectUrl: redirectUrl,
                PreventSubmissionUi: preventSubmissionUi
            },
            UnboundForm: unboundForm,
            FormLoadResponseType: formLoadResponseType,
            Error: formError,
            FeedbackPosition: feedbackPosition
        };
        return form;
    };
    FormUtility.getFormUrl = function (baseFormUrl) {
        // Add some random number (timestamp) to get the latest value from cache
        var currentTimestamp = new Date().getTime();
        var isNoCacheOptionEnabled = window.location.hash.includes(UriConstants.d365NoCacheParameter);
        return isNoCacheOptionEnabled ? this.addParameterToURL(baseFormUrl, "t", currentTimestamp.toString()) : baseFormUrl;
    };
    FormUtility.getSearchParams = function () {
        return new Proxy(new URLSearchParams(window.location.search), {
            get: function (searchParams, prop) { return searchParams.get(prop); }
        });
    };
    FormUtility.getReadableEventId = function (element) {
        var readableEventId = element.getAttribute(HtmlAttributes.dataReadableEventId);
        if (!readableEventId) {
            var params = FormUtility.getSearchParams();
            var queryStringReadableEventId = params[UriConstants.readableEventId];
            if (queryStringReadableEventId && queryStringReadableEventId.length) {
                readableEventId = queryStringReadableEventId;
            }
        }
        return readableEventId;
    };
    FormUtility.getEventRegistrationId = function () {
        var eventRegistrationId = "";
        var params = FormUtility.getSearchParams();
        var queryStringEventRegistrationId = params[UriConstants.eventRegistrationId];
        if (queryStringEventRegistrationId && queryStringEventRegistrationId.length) {
            eventRegistrationId = queryStringEventRegistrationId;
        }
        return eventRegistrationId;
    };
    FormUtility.isEventCancelationForm = function () {
        return !!FormUtility.getEventRegistrationId();
    };
    FormUtility.getHashParameters = function () {
        var searchParams = window.location.hash.length > 0 ? new URLSearchParams(window.location.hash.substring(1)) : new URLSearchParams();
        return new Proxy(searchParams, {
            get: function (searchParams, prop) { return searchParams.get(prop); }
        });
    };
    FormUtility.getPrefillAlias = function () {
        var hashParams = FormUtility.getHashParameters();
        var prefillId = hashParams[UriConstants.prefillAliasTrackingParameter];
        if (!prefillId) {
            var searchParams = this.getSearchParams();
            prefillId = searchParams[UriConstants.prefillAliasTrackingParameter];
        }
        return prefillId;
    };
    FormUtility.getFormFromQuery = function (query) {
        var _a;
        var forms = new Array();
        var formsMap = {};
        var formBlockElements = document.querySelectorAll(query);
        for (var i = 0; i < formBlockElements.length; i++) {
            var element = formBlockElements[i];
            var formToBeAdded = this.createFormDescriptorFromPlaceholderElement(element);
            if (formToBeAdded.FormId && ((_a = formToBeAdded.FormPlaceholders) === null || _a === void 0 ? void 0 : _a.length)) {
                var matchedForm = formsMap[formToBeAdded.FormId];
                if (matchedForm) {
                    matchedForm.FormPlaceholders.push(formToBeAdded.FormPlaceholders[0]);
                    continue;
                }
                forms.push(formToBeAdded);
                formsMap[formToBeAdded.FormId] = formToBeAdded;
            }
        }
        return forms;
    };
    FormUtility.getForm = function (formId) {
        var allForms = this.getAllForms();
        var forms = allForms.filter(function (form) { return form.FormId === formId; });
        if (forms.length > 0) {
            return forms[0];
        }
        return null;
    };
    FormUtility.getAllForms = function () {
        return FormUtility.getFormFromQuery("[".concat(HtmlAttributes.formCachedUrl, "]"));
    };
    FormUtility.isForm = function (form) {
        return form.FormId !== undefined;
    };
    FormUtility.addParameterToURL = function (url, name, param) {
        var areThereAnyQueryParams = url.split("?").length > 1;
        return areThereAnyQueryParams ? "".concat(url, "&").concat(name, "=").concat(param) : "".concat(url, "?").concat(name, "=").concat(param);
    };
    return FormUtility;
}());
export { FormUtility };
